import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import baseURL from '../../../../configs/api';
import { useTheme } from '../../../../components/hooks/useTheme';
import moment from 'moment';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getLocations } from '../../../../services/location';
import { format } from 'date-fns';
import { Alert, Snackbar } from '@mui/material';
import CustomFormatDatePicker from '../../../../components/common/CustomFormatDatePicker';
import useGeneral from '../../../../components/hooks/useGeneral';
import Cookies from 'js-cookie';
import useTenant from '../../../../components/hooks/useTenant';

type VehicleLocation = {
    id: number;
    vehicleId: number;
    latitude: number;
    longitude: number;
    speed: number;
    recordedAt: string;
    status: string;
    deviceId: string;
};

type Props = {
    idNumber: any;
};

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const GpsTracker = ({ idNumber }: Props) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { dateFormat }: any = useGeneral();
    const { handleTenantError }: any = useTenant();
    const [locationLogs, setLocationLogs] = useState<VehicleLocation[]>([]);
    const [logLoaded, setLogLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState<string>(moment()?.format("YYYY-MM-DD"));
    const [location, setLocation] = useState<any | null>(null);
    const [locationIsLoaded, setLocationIsLoaded] = useState<boolean>(false);
    const [center, setCenter] = useState<[number, number]>([0, 0]);
    const [polyline, setPolyline] = useState<[number, number][]>([]);
    const [showCopyPopup, setShowCopyPopup] = useState<boolean>(false);

    useEffect(() => {
        setLocationIsLoaded(false);
        getLocations(tenantId)
            .then(res => {
                const activeLocation = res?.filter((item: any) => item?.isActive);
                const data = activeLocation?.length > 0 ? activeLocation[0] : null;
                setLocation(data);
                setLocationIsLoaded(true);
                handleTenantError(null);
            })
            .catch((error) => {
                setLocationIsLoaded(true);
                setLocation(null);
                handleTenantError(error);
            })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (locationIsLoaded && locationLogs?.length <= 0 && logLoaded) {
            setCenter([
                location?.latitude || 0,
                location?.longitude || 0
            ]);
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationIsLoaded, logLoaded])

    const fetchData = () => {
        if (!idNumber?.state) return;

        setLoading(true);

        const isToday = moment().isSame(moment(date), 'day');

        const endpoint = isToday
            ? 'GetLatestVehicleLocationLogsPagingByVehicleIdAndByDate'
            : 'GetVehicleLocationLogsPagingByVehicleIdAndByDate';

        const url = `${baseURL}vehicleLocationLog/${endpoint}/${date},${idNumber.state},0,10`;

        setLogLoaded(false);

        fetch(url, {
            method: "GET",
            headers: {
                'X-Tenant-ID': tenantId,
            }
        })
            .then(res => res.json())
            .then(data => {
                if (data?.length > 0) {
                    setCenter(
                        [
                            data[0]?.latitude,
                            data[0]?.longitude
                        ]
                    );

                    setPolyline(
                        data?.map((loc: any) => [
                            loc?.latitude,
                            loc?.longitude
                        ])
                    );
                    setLoading(false);
                } else {
                    setPolyline([]);
                }

                setLocationLogs(data);
                setLogLoaded(true);
                handleTenantError(null);
            })
            .catch(err => {              
                handleTenantError(err);
                setCenter([0, 0]);
                setPolyline([]);
                setLocationLogs([]);
                setLogLoaded(true);
            });
    };

    useEffect(() => {
        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber, date]);

    const onRefresh = () => fetchData();


    return (
        <>

            <div className="py-[20px]">
                <div className="flex items-center justify-end pb-[0.5rem]">
                    <div className="flex items-center gap-x-[0.5rem]">
                        <button
                            type="button"
                            title="Refresh"
                            className={`group px-[0.3125rem] py-[0.25rem] rounded-lg border-2 ${themeStyles.main.borderColor} ${themeStyles.main.textSubHeadingColor} flex items-center justify-center cursor-pointer`}
                            onClick={onRefresh}
                        >
                            <RefreshIcon
                                name="Refresh"
                                className="group-active:rotate-180 transition-transform duration-300 ease-in-out"
                            />
                        </button>

                        <CustomFormatDatePicker
                            value={date}
                            onChange={(value) => {
                                setDate(moment(value).format("YYYY-MM-DD"))
                            }}
                            dateFormat={dateFormat}
                            required={true}
                            className={`px-[5px] py-[10px] ${dateFormat === "DD-MMM-YYYY" ? "w-[96px]" : "w-[86px]"} rounded-lg border ${themeStyles.main.borderColor} ${themeStyles.main.textSubHeadingColor} text-xs font-semibold outline-none bg-transparent`}
                            showValidMessage={false}
                            isModified={true}
                        />

                    </div>
                </div>

                <div className={`border ${themeStyles.main.borderColor} rounded-lg`}>
                    {
                        loading
                            ? (
                                <div className="h-[500px] flex items-center justify-center">
                                    <p className={`${themeStyles.main.textSubHeadingColor}`}>Loading map...</p>
                                </div>
                            ) : (
                                <MapContainer
                                    center={center}
                                    zoom={11}
                                    style={{
                                        height: '500px',
                                        width: '100%',
                                        borderRadius: "0.5rem"
                                    }}
                                >
                                    <TileLayer
                                        attribution="© OpenStreetMap contributors"
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />

                                    {
                                        locationLogs?.map((point, index) => (
                                            <Marker
                                                key={index}
                                                position={[point?.latitude, point?.longitude]}
                                            >
                                                <Popup>
                                                    <b>Status: </b>{point?.status}<br />
                                                    <b>Speed: </b>{point?.speed} km/h<br />
                                                    <b>Device: </b>{point?.deviceId}<br />
                                                    <b>Time: </b>{format(new Date(point?.recordedAt), "MMMM dd, yyyy hh:mm aaaaa'm'")}<br />
                                                    <b>LatLng: </b>
                                                    <a
                                                        href={`https://www.google.com/maps/place/${point?.latitude},${point?.longitude}`}
                                                        className="underline"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {point?.latitude}, {point?.longitude}
                                                    </a>
                                                    <button
                                                        onClick={() => {
                                                            if (showCopyPopup) {
                                                                setShowCopyPopup(false);
                                                            }
                                                            const latLng = `${point.latitude}, ${point.longitude}`;
                                                            navigator.clipboard.writeText(latLng);
                                                            setShowCopyPopup(true);
                                                            setTimeout(() => {
                                                                setShowCopyPopup(false);
                                                            }, 500);
                                                        }}
                                                        title="Copy LatLng"
                                                        type="button"
                                                        className="ml-[6px] bg-none border-none cursor-pointer my-auto"
                                                    >
                                                        <span className={`material-symbols-outlined text-[1rem]`}>
                                                            content_copy
                                                        </span>
                                                    </button>
                                                </Popup>
                                            </Marker>
                                        ))
                                    }

                                    <Polyline
                                        positions={polyline}
                                        color="blue"
                                    />
                                </MapContainer>
                            )
                    }
                </div>
            </div>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={showCopyPopup}
                autoHideDuration={1000}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    LatLng is copied
                </Alert>
            </Snackbar>
        </>
    );
};

export default GpsTracker;